import { useMemo } from 'react';

import { jwtDecode } from 'jwt-decode';

import { useCurrentUserQuery } from '@src/hooks/queries/users';
import { currentJwt } from '@src/utils/jwtHelpers';
import {
    hasPremierAccess as checkPremierAccess,
    hasFullAccess as checkFullAccess,
    hasAnyPremierAccess as checkAnyPremierAccess,
    isDigitalOnly as checkDigitalOnly,
    canViewSwag as checkCanViewSwag,
} from '@src/utils/premierAccess';

const useUserInfo = () => {
    const jwt = currentJwt();
    const { data } = useCurrentUserQuery({ enabled: Boolean(jwt) });
    const currentUser = data?.user;

    return useMemo(() => {
        let decodedJWT = null;
        if (jwt) {
            decodedJWT = jwtDecode(jwt);
        }

        if (!decodedJWT?.uuid || !currentUser?.uuid || decodedJWT.uuid !== currentUser.uuid) {
            return {
                uuid: null,
                name: null,
                email: null,
                agentCode: null,
                producerId: null,
                employeeId: null,
                accessLevel: null,
                limitedPremierAccess: null,
                activePremierAccess: null,
                premierAccessGroup: null,
                premierAccessRole: null,
                premierAccessLevel: null,
                salesChannel: null,
                exp: null,
                permissions: null,
                agency: null,
                premierBannerEnabled: null,
                pilot: null,
                isDigitalOnly: false,
                hasPremierAccess: false,
                hasFullAccess: false,
                hasAnyPremierAccess: false,
                canViewSwag: false,
                premierStatus: null,
                isPremier: false,
                isPremierPlus: false,
            };
        }

        const isDigitalOnly = checkDigitalOnly(currentUser);
        const hasPremierAccess = checkPremierAccess(currentUser);
        const hasFullAccess = checkFullAccess(currentUser);
        const hasAnyPremierAccess = checkAnyPremierAccess(currentUser);
        const canViewSwag = checkCanViewSwag(currentUser);
        const premierStatus = currentUser.premier_status;
        const isPremier = premierStatus === 'Premier';
        const isPremierPlus = premierStatus === 'Premier Plus';

        return {
            uuid: decodedJWT.uuid,
            name: currentUser.name,
            email: currentUser.email,
            agentCode: currentUser.agent_code,
            producerId: currentUser.producer_id,
            employeeId: currentUser.employee_id,
            accessLevel: currentUser.access_level,
            limitedPremierAccess: currentUser.limited_premier_access,
            activePremierAccess: currentUser.active_premier_access,
            premierAccessGroup: currentUser.premier_access_group,
            premierAccessRole: currentUser.premier_access_role,
            premierAccessLevel: currentUser.premier_access_level,
            salesChannel: currentUser.sales_channel,
            exp: decodedJWT.exp,
            permissions: currentUser.permissions,
            agency: currentUser.agency,
            premierBannerEnabled: currentUser.premier_banner_enabled,
            pilot: currentUser.pilot,
            isDigitalOnly,
            hasPremierAccess,
            hasFullAccess,
            hasAnyPremierAccess,
            canViewSwag,
            premierStatus,
            isPremier,
            isPremierPlus,
        };
    }, [jwt, currentUser]);
};

export { useUserInfo };
